import { Form, Formik, FormikProps } from "formik";
import { ApiNotificationTypeValue } from "../../../services/NotificationSettingsServices";
import dataConstants from "../../../constants/dataConstants";
import MultiSelectDropdown from "../../global/MultiselectDropdown";
import useOrganizations from "../../../services/utils/custom hooks/useOrganizations";
import useNotificationsType from "../../../services/utils/custom hooks/Notifications/useNotificationsType";
import useProfile from "../../../services/utils/custom hooks/useProfile";
import { useMemo } from "react";
import moment from "moment";
type NotificationFilters = {
  [key: string]: number | string;
};

interface ActionArgs {
  data?: any;
  onSuccess?: Function;
  onError?: Function;
  config?: any;
}

type initialValues = {
  organizationId: string;
  createdAt: string;
  notificationTypeId: string;
  // organizationLabel: string
  // dayLabel: string
  // typeLabel: string
};

function Render({
  errors,
  touched,
  values,
  setFieldValue,
  submitForm,
}: FormikProps<any>) {
  const { data, isLoading, isError } = useOrganizations();
  const { data: notificationsData } = useNotificationsType();
  const organizations = data?.data ?? [];
  const notificationTypes = notificationsData?.data ?? [];
  const organizationsList = organizations.map((organization: any) => ({
    label: organization.name,
    value: organization.id,
    selected: false,
  }));
  const typesSuggestions = notificationTypes.map(
    (type: ApiNotificationTypeValue) => ({
      label: type.name,
      value: type.id,
      selected: false,
    })
  );

  const { data: profile } = useProfile(); 

  // 1. Calculate if last login was > 7 days ago
  const isInactiveForWeek = useMemo(() => {
    const lastLogin = profile?.user?.lastLoginAt;
    if (!lastLogin) return true; // Default to "Last 7 Days" if no login data exists
    
    const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;
    return (new Date().getTime() - new Date(lastLogin).getTime()) > sevenDaysInMs;
  }, [profile]);

  // 2. Build the options dynamically
  const dynamicDaysOptions = useMemo(() => [
    { 
      label: "Today", 
      value: moment().format("YYYY-MM-DD"), 
      selected: false 
    },
    {
      // CONDITIONAL LOGIC HERE:
      label: isInactiveForWeek ? "Last 7 Days" : "Last 72 Hours",
      value: isInactiveForWeek 
        ? moment().subtract(7, "days").format("YYYY-MM-DD") 
        : moment().subtract(3, "days").format("YYYY-MM-DD"), 
      selected: false,
    },
    { 
      label: "Last 14 Days", 
      value: moment().subtract(14, "days").format("YYYY-MM-DD"), 
      selected: false 
    },
    { 
      label: "Last 28 Days", 
      value: moment().subtract(28, "days").format("YYYY-MM-DD"), 
      selected: false 
    },
    { 
      label: "Last 90 Days", 
      value: moment().subtract(90, "days").format("YYYY-MM-DD"), 
      selected: false 
    },
    { 
      label: "Last Year", 
      value: moment().subtract(1, "year").format("YYYY-MM-DD"), 
      selected: false 
    },
  ], [isInactiveForWeek]);

  function handleChange(name: string, value: string) {
    if (value === values[name]) {
      setFieldValue(name, "");
    } else {
      setFieldValue(name, value);
    }
    submitForm();
  }

  return (
    <Form className="">
      <div className="flex xs:flex-wrap md:flex-nowrap w-full justify-between items-center">
        <MultiSelectDropdown
          label="Filter By Type Of Notifications"
          required={false}
          errors={errors}
          placeholder="Select Type"
          touched={{ ...touched }}
          disabled={typesSuggestions.length === 0}
          name="notificationTypeId"
          id="notificationTypeId"
          onOptionClick={(item) => {
            handleChange("notificationTypeId", item.value);
          }}
          options={typesSuggestions.map((option: any) => {
            if (
              !!values.notificationTypeId &&
              option.value == values.notificationTypeId
            ) {
              return {
                ...option,
                selected: true,
              };
            }
            return {
              ...option,
            };
          })}
        />
        <MultiSelectDropdown
          label="Filter By Organization"
          required={false}
          errors={errors}
          touched={{ ...touched }}
          placeholder="Select Organization"
          disabled={organizationsList.length === 0}
          name="organizationId"
          id="organizationId"
          className="md:mx-5"
          onOptionClick={(item) => {
            handleChange("organizationId", item.value);
          }}
          options={organizationsList.map((option: any) => {
            if (
              !!values.organizationId &&
              option.value == values.organizationId
            ) {
              return {
                ...option,
                selected: true,
              };
            }
            return {
              ...option,
            };
          })}
          notificationsDropdown
        />
        <MultiSelectDropdown
          label="Filter By Days"
          required={false}
          errors={errors}
          placeholder="Select"
          touched={{ ...touched }}
          disabled={organizationsList.length === 0}
          name="createdAt"
          id="createdAt"
          onOptionClick={(item) => {
            handleChange("createdAt", item.value);
          }}
          options={dynamicDaysOptions.map((option: any) => {
            if (!!values.createdAt && option.value == values.createdAt) {
              return {
                ...option,
                selected: true,
              };
            }
            return {
              ...option,
            };
          })}
          className="md:mr-3"
          notificationsDropdown
        />
      </div>
    </Form>
  );
}

function NotificationsForm({
  setParams,
  setFilters,
}: {
  setParams: Function;
  setFilters: Function;
}): JSX.Element {
  const initialValues: initialValues = {
    organizationId: "",
    createdAt: "",
    notificationTypeId: "",
  };
  function createNotificationsURL(props: any) {
    let url = "";
    let urlParams: any = {
      notificationTypeId: "notificationTypeId",
      organizationId: "organizationId",
      createdAt: "createdAt",
    };

    const buildParams = (filters: any) => {
      const params = new URLSearchParams();

      Object.keys(filters).forEach(
        (key: string) => props[key] && params.append(filters[key], props[key])
      );

      return params.toString();
    };

    url = `${url}${buildParams(urlParams) ? `?${buildParams(urlParams)}` : ""}`;
    return url;
  }

  return (
    <Formik
      onSubmit={(values) => {
        setParams(createNotificationsURL(values));
        setFilters((prevState: any) => ({ ...prevState, ...values }));
      }}
      initialValues={initialValues}
      render={(props) => <Render {...props} />}
    />
  );
}

export default NotificationsForm;
